import { useCallback, useEffect, useState } from 'react';

import { matchPath, useNavigate } from 'react-router-dom';

const LOCALES = {
  en: {
    code: 'en-US',
    title: 'English',
  },
  es: {
    code: 'es-ES',
    title: 'Español',
  },
  de: {
    code: 'de-DE',
    title: 'Deutsch',
  },
  pt: {
    code: 'pt-BR',
    title: 'Português',
  },
  ru: {
    code: 'ru-RU',
    title: 'Русский',
  },
};

const LOCALE_STORAGE_KEY = 'locale';

export type LocaleId = keyof typeof LOCALES;

export type Locale = {
  code: string;
  title: string;
};

const getUserLocaleId = (): LocaleId => {
  const key = navigator.language.split('-')[0] as LocaleId;
  return Object.hasOwn(LOCALES, key) ? key : 'en';
};

export const useLocale = () => {
  const navigate = useNavigate();
  const [locale, setLocale] = useState<Locale>();

  useEffect(() => {
    const storageLocale = (localStorage.getItem(LOCALE_STORAGE_KEY) ??
      getUserLocaleId()) as LocaleId;

    const pathLocale = matchPath(
      {
        path: '/:lang',
        end: false,
      },
      document.location.pathname,
    )?.params.lang as LocaleId;

    const pathLocaleValid = !!LOCALES[pathLocale];
    if (!pathLocaleValid && storageLocale && storageLocale !== 'en') {
      navigate(
        `/${storageLocale}${document.location.pathname}${document.location.search}`,
      );
      return;
    }

    if (pathLocale === 'en' && storageLocale === 'en') {
      navigate(
        `/${document.location.pathname.slice(4)}${document.location.search}`,
      );
      return;
    }

    setLocale(LOCALES[pathLocale] ?? LOCALES[storageLocale] ?? LOCALES.en);
  }, [setLocale, navigate]);

  const selectLocale = useCallback(
    (selectedLocale: LocaleId) => {
      localStorage.setItem(LOCALE_STORAGE_KEY, selectedLocale);
      navigate(`/${selectedLocale}${document.location.search}`);
    },
    [navigate],
  );

  return {
    locale,
    locales: LOCALES,
    selectLocale,
  };
};
